import { AgendaSlotType } from '@omecare/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetDoctorSubstitutesQuery } from '@/hooks/queries/use-get-doctor-substitutes';
import { useGetPendingInvitationsByOffice } from '@/hooks/queries/use-get-pending-invitations-by-office';
import useDebounce from '@/hooks/use-debounce';
import { doctorFullnameTitleTrad } from '@/utils/doctor';

/**
 * The API sorts available substitutes first and paginates in memory, so this
 * page size is what caps the "Disponibles" group. Keep it above any realistic
 * office roster: a lower value silently truncates the group and makes the sheet
 * disagree with the calendar tooltip count.
 */
const SUBSTITUTES_PER_PAGE = 100;

type DoctorSubstituteOption = {
  id: string;
  value: string;
  label: string;
};

export type AvailableDoctorSubstitute = {
  id: string;
  label: string;
  priority?: number;
  installable?: boolean;
};

export type InvitedOption = {
  invitationId: string;
  label: string;
  email: string;
  slotCount: number;
};

export const selectDoctorSubstitutesViewModel = ({
  officeId,
  slotType,
  date,
  period,
}: {
  officeId: string;
  slotType: AgendaSlotType;
  date?: string;
  period?: string;
}) => {
  const { t } = useTranslation();

  const [searchDoctorSubstitute, setSearchDoctorSubstitute] =
    useState<string>();
  const debounceSearchDoctorSubstitute = useDebounce(searchDoctorSubstitute);

  const [availableSubstitutes, setAvailableSubstitutes] = useState<
    AvailableDoctorSubstitute[]
  >([]);
  const [unavailableOptions, setUnavailableOptions] = useState<
    DoctorSubstituteOption[]
  >([]);

  const { data: doctorSubstitutes, isPending: doctorSubstitutesLoading } =
    useGetDoctorSubstitutesQuery({
      page: 1,
      per_page: SUBSTITUTES_PER_PAGE,
      officeId,
      officeAccessType: slotType,
      filter: debounceSearchDoctorSubstitute,
      date,
      period,
    });

  const { data: pendingInvitationsData, isPending: pendingInvitationsLoading } =
    useGetPendingInvitationsByOffice(officeId);

  useEffect(() => {
    if (doctorSubstitutesLoading) {
      // While the query is pending, clear options so the combobox doesn't
      // show stale results from the previous query.
      setAvailableSubstitutes([]);
      setUnavailableOptions([]);
      return;
    }

    const withAvailability = date && period;
    const available: AvailableDoctorSubstitute[] = [];
    const unavailable: DoctorSubstituteOption[] = [];

    for (const doctorSubstitute of doctorSubstitutes?.data ?? []) {
      const doctorWithTitleTrad = doctorFullnameTitleTrad(doctorSubstitute);
      const label = t(doctorWithTitleTrad.key, doctorWithTitleTrad.params);

      if (withAvailability && doctorSubstitute.isAvailable === false) {
        unavailable.push({
          id: doctorSubstitute.id,
          value: doctorSubstitute.id,
          label,
        });
      } else if (withAvailability) {
        available.push({
          id: doctorSubstitute.id,
          label,
          priority: doctorSubstitute.priority ?? undefined,
          installable: doctorSubstitute.installable ?? undefined,
        });
      } else {
        // No date/period — everything goes to unavailableOptions
        unavailable.push({
          id: doctorSubstitute.id,
          value: doctorSubstitute.id,
          label,
        });
      }
    }

    setAvailableSubstitutes(available);
    setUnavailableOptions(unavailable);
  }, [doctorSubstitutes, doctorSubstitutesLoading, date, period, t]);

  // Compute invitedOptions: all pending invitations (PendingInvitationForOfficeItem
  // has no doctorSubstituteId field so cross-space dedup is not possible here)
  const invitedOptions: InvitedOption[] = (
    pendingInvitationsData?.data ?? []
  ).map((inv) => ({
    invitationId: inv.id,
    label: [inv.firstName, inv.lastName].filter(Boolean).join(' ') || inv.email,
    email: inv.email,
    slotCount: inv.slotCount,
  }));

  const getDoctorSubstituteByValue = (value?: string) => {
    if (!value) {
      return null;
    }
    const fromAvailable = availableSubstitutes.find((s) => s.id === value);
    if (fromAvailable) return fromAvailable;
    return unavailableOptions.find((o) => o.value === value) ?? null;
  };

  return {
    api: {
      setSearchDoctorSubstitute,
      getDoctorSubstituteByValue,
      getSelectLabel: (value?: string, invitationId?: string) => {
        const selectedDoctorSubstitute = getDoctorSubstituteByValue(value);
        if (selectedDoctorSubstitute) {
          return selectedDoctorSubstitute.label;
        }
        if (invitationId) {
          const invited = invitedOptions.find(
            (o) => o.invitationId === invitationId,
          );
          if (invited) return invited.label;
        }
        return t('book-agenda-slot-admin-form.doctor-substitute.placeholder');
      },
    },
    data: {
      availableSubstitutes,
      unavailableOptions,
      invitedOptions,
      hasAvailabilityGroups: !!(date && period),
      loading: doctorSubstitutesLoading,
      pendingInvitationsLoading,
    },
  };
};
